import React, { useState } from 'react';
import styles from './ContactForm.module.css';

const FUNCTION_URL = process.env.REACT_APP_CONTACT_FUNCTION_URL || '';
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

const initialFormState = {
  name: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
};

function validateForm(form) {
  const errors = {};

  if (!form.name.trim() || form.name.trim().length < 2) {
    errors.name = 'Name is required.';
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!form.email.trim() || !emailRegex.test(form.email.trim())) {
    errors.email = 'A valid email is required.';
  }

  if (form.phone && !/^[\d\s\-+().]{7,20}$/.test(form.phone)) {
    errors.phone = 'Invalid phone number.';
  }

  if (!form.subject.trim()) {
    errors.subject = 'Subject is required.';
  }

  if (!form.message.trim() || form.message.trim().length < 10) {
    errors.message = 'Message must be at least 10 characters.';
  }

  return errors;
}

function getRecaptchaToken() {
  return new Promise((resolve, reject) => {
    if (!window.grecaptcha || !RECAPTCHA_SITE_KEY) {
      reject(new Error('reCAPTCHA not loaded'));
      return;
    }
    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(RECAPTCHA_SITE_KEY, { action: 'contact' })
        .then(resolve)
        .catch(reject);
    });
  });
}

function ContactForm() {
  const [form, setForm] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const [serverError, setServerError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setStatus('loading');
    setErrors({});
    setServerError('');

    try {
      const recaptchaToken = await getRecaptchaToken();

      const response = await fetch(FUNCTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || undefined,
          subject: form.subject.trim(),
          message: form.message.trim(),
          recaptchaToken,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Something went wrong. Please try again.');
      }

      setStatus('success');
      setForm(initialFormState);
    } catch (err) {
      setStatus('error');
      setServerError(err.message || 'Failed to send message. Please try again.');
    }
  };

  if (status === 'success') {
    return (
      <div className={styles.successMessage}>
        <span className={styles.successIcon}>&#10003;</span>
        <h3>Message Sent</h3>
        <p>Thanks for reaching out. We'll get back to you within 24 hours.</p>
        <button
          className={styles.resetButton}
          onClick={() => setStatus('idle')}
        >
          Send Another Message
        </button>
      </div>
    );
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      <div className={styles.formRow}>
        <div className={styles.formGroup}>
          <label htmlFor="contact-name" className={styles.label}>
            Name <span className={styles.required}>*</span>
          </label>
          <input
            id="contact-name"
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
            placeholder="Your name"
            maxLength={100}
          />
          {errors.name && <span className={styles.errorText}>{errors.name}</span>}
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="contact-email" className={styles.label}>
            Email <span className={styles.required}>*</span>
          </label>
          <input
            id="contact-email"
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
            placeholder="you@example.com"
          />
          {errors.email && <span className={styles.errorText}>{errors.email}</span>}
        </div>
      </div>

      <div className={styles.formRow}>
        <div className={styles.formGroup}>
          <label htmlFor="contact-phone" className={styles.label}>
            Phone <span className={styles.optional}>(optional)</span>
          </label>
          <input
            id="contact-phone"
            type="tel"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            className={`${styles.input} ${errors.phone ? styles.inputError : ''}`}
            placeholder="(555) 123-4567"
          />
          {errors.phone && <span className={styles.errorText}>{errors.phone}</span>}
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="contact-subject" className={styles.label}>
            Subject <span className={styles.required}>*</span>
          </label>
          <input
            id="contact-subject"
            type="text"
            name="subject"
            value={form.subject}
            onChange={handleChange}
            className={`${styles.input} ${errors.subject ? styles.inputError : ''}`}
            placeholder="Project inquiry"
            maxLength={200}
          />
          {errors.subject && <span className={styles.errorText}>{errors.subject}</span>}
        </div>
      </div>

      <div className={styles.formGroup}>
        <label htmlFor="contact-message" className={styles.label}>
          Message <span className={styles.required}>*</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          value={form.message}
          onChange={handleChange}
          className={`${styles.textarea} ${errors.message ? styles.inputError : ''}`}
          placeholder="Tell us about your project, goals, and timeline..."
          rows={6}
          maxLength={5000}
        />
        {errors.message && <span className={styles.errorText}>{errors.message}</span>}
      </div>

      {serverError && (
        <div className={styles.serverError}>{serverError}</div>
      )}

      <button
        type="submit"
        className={styles.submitButton}
        disabled={status === 'loading'}
      >
        {status === 'loading' ? 'Sending...' : 'Send Message'}
      </button>
    </form>
  );
}

export default ContactForm;
